import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Section, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { toast, Toaster } from "sonner";
import { ExternalLink, RefreshCw, Cloud, Users, UserPlus } from "lucide-react";

export default function SuckMyLead() {
  const [url, setUrl] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/sml-info").then((r) => setUrl(r.data.url)).catch(() => {});
  }, []);

  const openSml = () => {
    if (!url) return toast.error("URL Suck My Lead non configurée.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const sync = async () => {
    if (!window.confirm("Synchroniser les vendeurs et les nouveaux prospects depuis Suck My Lead ?\n(Aucun email de présentation ne sera envoyé.)")) return;
    setSyncing(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/sync-suck-my-lead?import_prospects=true&send_presentation=false");
      setResult(data);
      toast.success("Synchronisation terminée.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la synchronisation.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-8" data-testid="suck-my-lead-page">
      <Toaster position="top-right" richColors />
      <PageHeader title="Suck My Lead" subtitle="Source des leads — synchronisation & accès" />

      <div className="max-w-3xl space-y-6">
        <Section title="Accès à la plateforme" actions={<Cloud className="w-4 h-4 text-[#B8873A]" />}>
          <div className="p-5">
            <p className="text-sm text-slate-500 mb-4">
              Ouvrez la plateforme Suck My Lead dans un nouvel onglet pour gérer vos campagnes et vos sources de leads.
            </p>
            <PrimaryButton onClick={openSml} data-testid="open-sml-btn">
              <span className="flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Ouvrir Suck My Lead</span>
            </PrimaryButton>
            {url && <div className="mt-3 text-xs text-slate-400 break-all">{url}</div>}
          </div>
        </Section>

        <Section title="Synchronisation" actions={<RefreshCw className="w-4 h-4 text-[#B8873A]" />}>
          <div className="p-5">
            <p className="text-sm text-slate-500 mb-4">
              Importe les <strong>vendeurs</strong> et les <strong>nouveaux prospects</strong> depuis Suck My Lead
              vers le CRM. L'opération est sûre et idempotente : aucun doublon, et <strong>aucun email de présentation</strong> n'est envoyé.
            </p>
            <SecondaryButton onClick={sync} disabled={syncing} data-testid="sync-sml-btn">
              <span className="flex items-center gap-2">
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Synchronisation en cours…" : "Synchroniser maintenant"}
              </span>
            </SecondaryButton>

            {result && (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sync-result">
                <div className="border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    <Users className="w-3.5 h-3.5" /> Vendeurs
                  </div>
                  <div className="text-sm text-slate-700">
                    {result.conseillers?.fetched ?? 0} récupérés · {result.conseillers?.created ?? 0} créés · {result.conseillers?.updated ?? 0} à jour
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    <UserPlus className="w-3.5 h-3.5" /> Prospects
                  </div>
                  <div className="text-sm text-slate-700">
                    {result.prospects?.created ?? 0} nouveaux · {result.prospects?.duplicates_skipped ?? 0} déjà présents
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
